// Pure helpers for the two-tag category chip's one-tap scope flip
// (personal <-> business), per the founder decision 2026-09-08: "Flip this
// one, offer apply-to-merchant inline." A flip fires the immediate PATCH
// when an EXACT-suffix counterpart leaf exists in the taxonomy
// (`docs/canonical/product/category-taxonomy.md`: leaf slugs are
// scope-prefixed, e.g. `personal_travel` / `business_travel`), or when the
// slug is one of the small curated pairs below. Nothing is invented outside
// those two cases -- `personal_education` has no automatic business
// counterpart even though the taxonomy has other business leaves; personal
// and business income leaves are scope-only with none at all. Transfer
// leaves never flip (single neutral scope, D-decision in the task brief).
import type { Category } from '@/api/hooks/useCategories';

export type CategoryScope = 'personal' | 'business' | 'transfer';

const SCOPE_PREFIX: Record<'personal' | 'business', string> = {
  personal: 'personal_',
  business: 'business_',
};

// A small, curated map of unambiguous personal<->business counterparts that
// don't share an exact suffix (deferrable 1, orchestrator-approved
// 2026-09-08). Only pairs where the founder's one-tap intent is unambiguous
// -- meals, car/truck, utilities -- are included; every other non-exact-
// suffix leaf (e.g. `personal_education`) still opens the scoped picker
// rather than guessing.
const CURATED_COUNTERPARTS: Record<string, string> = {
  personal_restaurants_coffee: 'business_meals',
  business_meals: 'personal_restaurants_coffee',
  personal_auto_transport: 'business_car_truck',
  business_car_truck: 'personal_auto_transport',
  personal_utilities_phone: 'business_utilities',
  business_utilities: 'personal_utilities_phone',
};

/** Derives scope from a leaf's slug prefix; null when unrecognized. */
export function scopeOfSlug(slug: string | null | undefined): CategoryScope | null {
  if (!slug) return null;
  if (slug.startsWith('personal_')) return 'personal';
  if (slug.startsWith('business_')) return 'business';
  if (slug.startsWith('transfer_') || slug === 'transfer') return 'transfer';
  return null;
}

/** The flip target for a flippable scope. */
export function counterpartScope(scope: 'personal' | 'business'): 'personal' | 'business' {
  return scope === 'personal' ? 'business' : 'personal';
}

/**
 * Derives scope from the category ROW's `kind`/`is_business` -- the single
 * source of truth (ENG-R1-001, orchestrator decision 2026-09-08): a custom
 * leaf's slug (`backend/services/categories/crud.py::_slugify`) carries NO
 * `personal_`/`business_` prefix at all, so `scopeOfSlug` alone silently
 * loses scope/flip/chooser for every user-created type. Returns null only
 * when `category` itself is missing or its `kind` is unresolved (the
 * `__taxonomy_marker__` sentinel).
 */
export function scopeOfCategory(category: Category | null | undefined): CategoryScope | null {
  if (!category) return null;
  if (category.kind === 'transfer') return 'transfer';
  if (category.kind === 'expense' || category.kind === 'income') {
    return category.is_business ? 'business' : 'personal';
  }
  return null;
}

/**
 * The leaf in the other scope sharing the exact same suffix, or null when
 * `currentSlug` is not a personal/business leaf, or no exact counterpart
 * exists in `categories` (the full taxonomy from `useCategories`).
 *
 * Scope is resolved from the current leaf's ROW (`scopeOfCategory`) when it
 * is present in `categories`, falling back to the slug prefix only when the
 * row can't be found (ENG-R1-001) -- so a real custom leaf (unprefixed
 * slug, e.g. `rideshare`) still resolves the right scope; it just never has
 * an exact-suffix counterpart (no prefix to swap), so it falls through to
 * `flipTarget`'s same-kind catch-all below.
 */
export function findScopeCounterpart(currentSlug: string, categories: Category[]): Category | null {
  const current = categories.find((category) => category.slug === currentSlug);
  const scope = scopeOfCategory(current) ?? scopeOfSlug(currentSlug);
  if (scope !== 'personal' && scope !== 'business') return null;
  const prefix = SCOPE_PREFIX[scope];
  if (!currentSlug.startsWith(prefix)) {
    const curatedTargetSlug = CURATED_COUNTERPARTS[currentSlug];
    if (!curatedTargetSlug) return null;
    return categories.find((category) => category.parent_id !== null && category.slug === curatedTargetSlug) ?? null;
  }
  const suffix = currentSlug.slice(prefix.length);
  if (!suffix) return null;
  const exactTargetSlug = `${SCOPE_PREFIX[counterpartScope(scope)]}${suffix}`;
  const exactMatch = categories.find((category) => category.parent_id !== null && category.slug === exactTargetSlug);
  if (exactMatch) return exactMatch;

  const curatedTargetSlug = CURATED_COUNTERPARTS[currentSlug];
  if (!curatedTargetSlug) return null;
  return categories.find((category) => category.parent_id !== null && category.slug === curatedTargetSlug) ?? null;
}

/**
 * The one-tap scope flip's target leaf (D-A, founder feedback 2026-09-08:
 * "it should flip instead of opening a long drop down list"). Always
 * returns a leaf for a personal/business slug with a resolvable kind --
 * never opens a picker:
 *
 *   1. the exact-suffix or curated counterpart (`findScopeCounterpart`), or
 *   2. the target scope's catch-all leaf of the SAME KIND --
 *      `business_other`/`personal_other` for an expense leaf,
 *      `business_other_income`/`personal_other_income` for an income leaf.
 *
 * Returns null only when `slug` is not a personal/business leaf (transfer
 * never flips), or the current leaf's `kind` can't be resolved from
 * `categories` (e.g. an unrecognized/stale slug) -- never invents a target.
 */
export function flipTarget(slug: string, categories: Category[]): Category | null {
  const current = categories.find((category) => category.slug === slug);
  const scope = scopeOfCategory(current) ?? scopeOfSlug(slug);
  if (scope !== 'personal' && scope !== 'business') return null;

  const counterpart = findScopeCounterpart(slug, categories);
  if (counterpart) return counterpart;

  const kind = current?.kind;
  if (kind !== 'expense' && kind !== 'income') return null;

  const targetScope = counterpartScope(scope);
  const catchAllSlug = kind === 'expense' ? `${targetScope}_other` : `${targetScope}_other_income`;
  return categories.find((category) => category.parent_id !== null && category.slug === catchAllSlug) ?? null;
}

/**
 * The taxonomy filtered to one scope's system parents plus their leaves.
 * Historically constrained the standalone `CategoryPicker` panel (deleted --
 * `TypeChooser`'s own scope/any-mode grouping superseded it); kept here as a
 * small, independently-tested pure helper in case a future scoped picker
 * needs the same filter again.
 */
export function categoriesInScope(categories: Category[], scope: 'personal' | 'business'): Category[] {
  const prefix = SCOPE_PREFIX[scope];
  const parentIds = new Set(
    categories.filter((category) => category.parent_id === null && category.slug.startsWith(prefix)).map((category) => category.category_id),
  );
  return categories.filter(
    (category) =>
      (category.parent_id === null && parentIds.has(category.category_id)) ||
      (category.parent_id !== null && parentIds.has(category.parent_id)),
  );
}
